import React, { ElementType, ReactNode, forwardRef, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Box, BoxProps, SvgIcon, SvgIconProps, useTheme } from '@mui/material';
import { LoadingText, LoadingTextProps } from './LoadingText';
import { ProgressState, ProgressStateProps } from './ProgressState';

export interface SpinnerProps extends BoxProps<ElementType> {
  /**
   * Value of progress
   */
  value?: number;
  /**
   * Affects component size
   * @default medium
   */
  size?: 'small' | 'medium' | number;
  /**
   * Stroke width of circle
   * @default 16
   */
  thickness?: number;
  /**
   * State indicates whether the component should display loader or spinner for the loading state or message with a icon
   * @default loading
   */
  state?: 'loading' | ProgressStateProps['state'];
  /**
   * Message when success appears
   */
  successMessage?: ProgressStateProps['successMessage'];
  /**
   * Message when error appears
   */
  errorMessage?: ProgressStateProps['errorMessage'];
  /**
   * Properties applied to ProgressState
   */
  progressStateProps?: Omit<ProgressStateProps, 'successMessage' | 'errorMessage' | 'state'>;
  /**
   * Properties applied to SVG
   */
  svgIconProps?: SvgIconProps;
  /**
   * Additional label before LoadingText
   */
  label?: ReactNode;
  /**
   * Properties applied to LoadingText
   */
  loadingTextProps?: LoadingTextProps;
}

/**
 * A styled loader component that displays animated svg circle
 */
export const Spinner: React.FC<SpinnerProps> = forwardRef(
  (
    {
      sx,
      className,
      children,
      size = 'medium',
      thickness = 16,
      value,
      svgIconProps,
      loadingTextProps,
      label,
      state = 'loading',
      successMessage,
      errorMessage,
      progressStateProps,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const { sx: svgIconSx, ...restSvgIcon } = svgIconProps ?? {};

    // smooth js transition between values
    const [displayValue, setDisplayValue] = useState(Number.isNaN(value) ? 0 : (value ?? 15));
    const targetValueRef = useRef<number>(null);
    const rafRef = useRef<number>(null);
    useEffect(() => {
      targetValueRef.current = Number.isNaN(value) ? 0 : (value ?? 15);

      const animate = () => {
        setDisplayValue((prev) => {
          const diff = targetValueRef.current - prev;
          const step = diff * 0.5;
          const next = prev + step;

          // small diff or reset happens immediately
          if (Math.abs(diff) < 0.1 || targetValueRef.current === 0) {
            return targetValueRef.current;
          } else {
            rafRef.current = requestAnimationFrame(animate);
            return next;
          }
        });
      };
      rafRef.current = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(rafRef.current!);
    }, [value]);

    const { radius, circumference, lineCap, gap, minSegmentPercentForFullGap } = useMemo(() => {
      const _radius = 50 - thickness / 2;
      const _circumference = 2 * Math.PI * _radius;
      const _lineCap = thickness / 2;
      const _gap = _lineCap;
      const _minSegmentPercentForFullGap = ((thickness + _gap) / _circumference) * 100;

      return {
        radius: _radius,
        circumference: _circumference,
        lineCap: _lineCap,
        gap: _gap,
        minSegmentPercentForFullGap: _minSegmentPercentForFullGap,
      };
    }, [thickness]);
    const obtainValues = useCallback(
      (v: number) => {
        const _reversedValue = 100 - v;
        const _reversedRotation = 360 * (v / 100);
        const _effectiveGap = gap * Math.max(0, Math.min(1, Math.min(v, _reversedValue) / minSegmentPercentForFullGap));
        const _dashLength = Math.max((v / 100) * circumference - thickness - _effectiveGap, 0);
        const _dashOffset = -(lineCap + _effectiveGap / 2);
        const _reversedDashLength = Math.max((_reversedValue / 100) * circumference - thickness - _effectiveGap, 0);

        return {
          reversedRotation: _reversedRotation,
          dashLength: _dashLength,
          dashOffset: _dashOffset,
          reversedDashLength: _reversedDashLength,
        };
      },
      [thickness],
    );
    const values = obtainValues(displayValue);
    const [valuesSmall, valuesLarge] = value === undefined ? [obtainValues(15), obtainValues(75)] : [values, values];

    if (state !== 'loading') {
      return (
        <ProgressState
          ref={ref}
          size={size}
          state={state}
          successMessage={successMessage}
          errorMessage={errorMessage}
          {...progressStateProps}
        />
      );
    }

    return (
      <Box
        data-testid="Spinner"
        role="progressbar"
        className={['FlowSpinner-root', className].filter(Boolean).join(' ')}
        ref={ref}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          ...sx,
        }}
        {...(value !== undefined && {
          ['aria-valuenow']: value,
          ['aria-valuemin']: 0,
          ['aria-valuemax']: 100,
        })}
        {...rest}
      >
        <SvgIcon
          aria-live="polite"
          viewBox="0 0 100 100"
          sx={{
            // size is also applied in ProgressState and LoadingText
            ...(size === 'small' && {
              width: `16px`,
              height: `16px`,
            }),
            ...(size === 'medium' && {
              width: `24px`,
              height: `24px`,
            }),
            ...(typeof size === 'number' && {
              width: `${size}px`,
              height: `${size}px`,
            }),
            color: theme.palette.mode === 'dark' ? 'brand.secondary' : 'brand.primary',
            // extra animation if value is not provided
            ...(value === undefined && {
              '@keyframes flow-spinner-indeterminate': {
                '0%': { transform: 'rotateZ(0)' },
                '100%': { transform: 'rotateZ(360deg)' },
              },
              '@keyframes flow-spinner-indeterminate-group': {
                '0%, 25%': { transform: 'rotateZ(0)' },
                '70%': { transform: 'rotateZ(45deg)' },
                '100%': { transform: 'rotateZ(360deg)' },
              },
              '@keyframes flow-spinner-indeterminate-circle-primary': {
                '0%, 25%, 100%': {
                  strokeDasharray: `${valuesSmall.dashLength} ${circumference}`,
                  strokeDashOffset: valuesSmall.dashOffset,
                },
                '70%': {
                  strokeDasharray: `${valuesLarge.dashLength} ${circumference}`,
                  strokeDashOffset: valuesLarge.dashOffset,
                },
              },
              '@keyframes flow-spinner-indeterminate-circle-secondary': {
                '0%, 25%, 100%': {
                  strokeDasharray: `${valuesSmall.reversedDashLength} ${circumference}`,
                  strokeDashOffset: valuesSmall.dashOffset,
                },
                '70%': {
                  strokeDasharray: `${valuesLarge.reversedDashLength} ${circumference}`,
                  strokeDashOffset: valuesLarge.dashOffset,
                },
              },
              '@keyframes flow-spinner-indeterminate-group-secondary': {
                '0%, 25%, 100%': { transform: `rotate(${valuesSmall.reversedRotation}deg)` },
                '70%': { transform: `rotate(${valuesLarge.reversedRotation}deg)` },
              },
              // assign animation to elements
              animation: '2s linear infinite flow-spinner-indeterminate',
              '& > g': {
                animation: '2s ease-in-out infinite flow-spinner-indeterminate-group',
                transformOrigin: '50% 50%',
                '& circle': {
                  animation: '2s ease-in-out infinite flow-spinner-indeterminate-circle-primary',
                },
                '& g': {
                  animation: '2s ease-in-out infinite flow-spinner-indeterminate-group-secondary',
                  '& circle': {
                    animation: '2s ease-in-out infinite flow-spinner-indeterminate-circle-secondary',
                  },
                },
              },
            }),
            ...svgIconSx,
          }}
          {...restSvgIcon}
        >
          <g>
            {(values.reversedDashLength > 0 || displayValue === 0) && (
              <g style={{ transform: `rotate(${values.reversedRotation}deg)`, transformOrigin: '50% 50%' }}>
                <circle
                  className="FlowSpinner-circle-secondary"
                  cx="50"
                  cy="50"
                  r={radius}
                  fill="none"
                  stroke={
                    theme.palette.mode === 'dark' ? theme.palette.onSurface.os700 : theme.palette.transparent.secondary
                  }
                  strokeWidth={thickness}
                  strokeLinecap="round"
                  strokeDasharray={`${values.reversedDashLength} ${circumference}`}
                  strokeDashoffset={values.dashOffset}
                  {...(displayValue === 0 && {
                    strokeLinecap: 'butt',
                    strokeDasharray: 'none',
                    strokeDashoffset: 'none',
                  })}
                />
              </g>
            )}
            {(values.dashLength > 0 || displayValue === 100) && (
              <circle
                className="FlowSpinner-circle-primary"
                cx="50"
                cy="50"
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth={thickness}
                strokeLinecap="round"
                strokeDasharray={`${values.dashLength} ${circumference}`}
                strokeDashoffset={values.dashOffset}
                {...(displayValue === 100 && {
                  strokeLinecap: 'butt',
                  strokeDasharray: 'none',
                  strokeDashoffset: 'none',
                })}
              />
            )}
          </g>
        </SvgIcon>
        {label && value !== undefined && (
          <LoadingText
            data-testid="Spinner.percentage"
            {...(typeof size !== 'number' && { size: size })}
            value={value}
            label={label !== true ? label : undefined}
            {...loadingTextProps}
          />
        )}
      </Box>
    );
  },
);
